// Geo sampling helpers for the Edit-Route-Master "Waypoints" panel.
//
// Ported from the TLS frontend (src/Components/HereMap/libs/geoLib.js) and the
// backend's existing port (backend/controller/here_polyline.py), using haversine
// instead of turf so there is no turf dependency.
//
// Coordinate convention: every function takes and returns `[lat, lng]` pairs.
// (TLS fed turf GeoJSON `[lng, lat]` and swapped on output; we standardize on
// `[lat, lng]` throughout to avoid swap bugs.)

// Mean earth radius (km), matching @turf/helpers (6371008.8 m) so the sampling
// output lines up with the TLS turf-based version and the backend port.
const EARTH_RADIUS_KM = 6371.0088;

const toRad = (deg) => (deg * Math.PI) / 180;

// Parse a stored/returned waypoints value into [[lat, lng], ...] numeric pairs.
// The canonical format is nested [[lat, lng], ...] (matching TLS), but legacy
// rows saved before the migration are flat [lat1, lng1, lat2, ...] — accept both.
export const parseWaypointPairs = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const pairs = [];
  if (Array.isArray(raw[0])) {
    // Nested [[lat, lng], ...] (current format).
    for (let i = 0; i < raw.length; i++) {
      const lat = Number(raw[i][0]);
      const lng = Number(raw[i][1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) pairs.push([lat, lng]);
    }
  } else {
    // Legacy flat [lat1, lng1, lat2, lng2, ...].
    for (let i = 0; i + 1 < raw.length; i += 2) {
      const lat = Number(raw[i]);
      const lng = Number(raw[i + 1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) pairs.push([lat, lng]);
    }
  }
  return pairs;
};

// Great-circle distance in km between two [lat, lng] points.
const haversineKm = (a, b) => {
  const lat1 = Number(a[0]);
  const lng1 = Number(a[1]);
  const lat2 = Number(b[0]);
  const lng2 = Number(b[1]);
  const dlat = toRad(lat2 - lat1);
  const dlng = toRad(lng2 - lng1);
  const h =
    Math.sin(dlat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dlng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

// Total great-circle length (km) of a polyline given as [[lat, lng], ...].
export const lineDistanceKm = (coords) => {
  if (!coords || coords.length < 2) return -1;
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += haversineKm(coords[i], coords[i + 1]);
  }
  return total;
};

// Sample a point every `intervalKm` along the line. Steps 1.. while
// step*interval < total. Returns [[lat, lng], ...].
export const pointsAlongLine = (coords, intervalKm) => {
  const interval = Number(intervalKm);
  if (!coords || coords.length < 2 || !(interval > 0)) return [];

  const total = lineDistanceKm(coords);
  const result = [];
  for (let step = 1; step * interval < total; step++) {
    const target = step * interval;
    let travelled = 0;
    let sampled = coords[coords.length - 1];
    for (let i = 0; i < coords.length - 1; i++) {
      const seg = haversineKm(coords[i], coords[i + 1]);
      if (travelled + seg >= target) {
        const frac = seg === 0 ? 0 : (target - travelled) / seg;
        const lat = coords[i][0] + (coords[i + 1][0] - coords[i][0]) * frac;
        const lng = coords[i][1] + (coords[i + 1][1] - coords[i][1]) * frac;
        sampled = [lat, lng];
        break;
      }
      travelled += seg;
    }
    result.push([sampled[0], sampled[1]]);
  }
  return result;
};

// Distance-conditioned sampling: every 1 km for routes < 10 km, every 2 km for
// 10-30 km, otherwise ~30 segments. Returns [[lat, lng], ...].
export const waypointsByDistanceCond = (coords) => {
  let interval = -1;
  if (coords && coords.length > 2) {
    const d = lineDistanceKm(coords);
    if (d < 10) {
      interval = 1;
    } else if (d >= 10 && d < 30) {
      interval = 2;
    } else {
      interval = d / 30;
    }
  }
  if (interval > 0) {
    return pointsAlongLine(coords, interval);
  }
  return [];
};

// "Auto" sampling: push a point wherever the turn angle between consecutive
// segments exceeds `threshold` degrees, skipping points within 300 m of the
// previously kept one. Returns [[lat, lng], ...].
//
// NOTE: the turn-angle magnitude is invariant under a lat/lng axis swap (it is
// |atan2(cross, dot)|), so the angle arithmetic is copied verbatim from TLS.
// Only the output and the 300 m dedup are adapted to the [lat, lng] convention.
export const waypointsByBearingCond = (coords, threshold = 45) => {
  const result = [];
  if (!coords || coords.length < 3) return result;

  for (let idx = 1; idx < coords.length - 1; idx++) {
    {
      const dAx = coords[idx][1] - coords[idx - 1][1];
      const dAy = coords[idx][0] - coords[idx - 1][0];
      const dBx = coords[idx + 1][1] - coords[idx][1];
      const dBy = coords[idx + 1][0] - coords[idx][0];
      let angle = Math.atan2(dAx * dBy - dAy * dBx, dAx * dBx + dAy * dBy);
      if (angle < 0) {
        angle = angle * -1;
      }
      const degreeAngle = angle * (180 / Math.PI);

      if (degreeAngle > threshold) {
        const pt = [coords[idx][0], coords[idx][1]];
        if (result.length) {
          // Distance between waypoints must be greater than 300 m.
          const dist = haversineKm(result[result.length - 1], pt);
          if (dist > 0.3) {
            result.push(pt);
          }
        } else {
          result.push(pt);
        }
      }
    }
  }
  return result;
};
